import { useEffect, useState } from 'react';

import { FCC } from '@eluve/components';
import { useNamedLogger } from '@eluve/logger';
import { useAssignedTenantIdFromParams } from '@eluve/session-helpers';
import {
  PeriodicLocalFileScanMachineProvider,
  PeriodicRemoteFileSyncMachineProvider,
  readAllAppointmentFilesFromOpfs,
  useDb,
  useUserFileSystemStore,
} from '@eluve/user-local-files';

/**
 * This component is a bridge between the PeriodicFileSyncProvider and the web app.
 * It provides online/offline status to the file sync machine.
 */
export const PeriodicFileSyncProviderBridge: FCC = ({ children }) => {
  const logger = useNamedLogger('PeriodicFileSyncProviderBridge');
  const db = useDb();

  const [isOnline, setIsOnline] = useState(navigator.onLine);

  const tenantId = useAssignedTenantIdFromParams();

  const fileSystem = useUserFileSystemStore((state) => state.userFileSystem);

  useEffect(() => {
    const setOnline = () => setIsOnline(true);
    const setOffline = () => setIsOnline(false);

    window.addEventListener('online', setOnline);
    window.addEventListener('offline', setOffline);

    return () => {
      window.removeEventListener('online', setOnline);
      window.removeEventListener('offline', setOffline);
    };
  }, []);

  return (
    <PeriodicRemoteFileSyncMachineProvider
      tenantId={tenantId}
      isOnline={isOnline}
    >
      {children}
      <PeriodicLocalFileScanMachineProvider
        getFileReferences={async () => {
          const dbFileRecords = await db.appointmentFiles.toArray();
          const opfsFiles = await readAllAppointmentFilesFromOpfs(
            fileSystem,
            logger,
            dbFileRecords,
          );

          return {
            dbFileRecords,
            opfsFiles,
          };
        }}
        appointmentFilesTable={db.appointmentFiles}
      />
    </PeriodicRemoteFileSyncMachineProvider>
  );
};
